// ---------------------------------------------------------------------------
// Inbox mappers
// Maps inbox_items (from /api/inbox) to the Thread interface used by the UI
// ---------------------------------------------------------------------------

import { Building2, User } from 'lucide-react'
import type { InboxItem } from '@/hooks/use-inbox'
import type { Workspace } from '@/providers/workspace-provider'
import {
  pickInboxPreviewSource,
  toInboxPreviewPlain,
  toInboxTitlePlain,
} from './inbox-preview-plain'
import type { Kind, Status, Thread, InboxWorkspace } from './types'

/**
 * Compute a human-readable relative age string from an ISO timestamp.
 * e.g. "14m", "2h", "1d"
 */
export function formatAge(timestamp: string): string {
  const now = Date.now()
  const then = new Date(timestamp).getTime()
  if (isNaN(then)) return ''
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'now'
  if (diffMin < 60) return `${diffMin}m`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `${diffH}h`
  const diffD = Math.floor(diffH / 24)
  return `${diffD}d`
}

/** Map InboxItemType (+ optional threads.kind) to Thread Kind */
function mapKind(type: string, meta?: Record<string, unknown>): Kind {
  if (meta?.organization_invite_id != null) return 'invite'
  const threadKind = meta?.threadKind != null ? String(meta.threadKind) : ''
  if (threadKind === 'agent_chat') return 'chat'
  if (threadKind === 'flow_output') return 'flow'
  // Legacy flow.run.failed threads used kind=system; Rule 4 is Flows, not Suggestions.
  if (threadKind === 'system') return 'flow'
  if (threadKind === 'extension_event') return 'extension'
  if (threadKind === 'voice') return 'voice'
  if (threadKind === 'email') return 'email'
  if (meta?.dispatch_status || meta?.agentId) return 'agent'
  switch (type) {
    case 'voice_memo':    return 'voice'
    case 'agent_message': return 'agent'
    case 'flow_output':   return 'flow'
    // kindToType maps extension_event -> external; accept both.
    case 'extension_event':
    case 'external':      return 'extension'
    case 'email':         return 'email'
    case 'suggestion':    return 'suggestion'
    case 'invite':        return 'invite'
    default:              return 'suggestion'
  }
}

/** Map InboxItemStatus to Thread Status */
function mapStatus(status: string): Status {
  switch (status) {
    case 'new':      return 'unread'
    case 'unread':   return 'unread'
    case 'read':     return 'read'
    case 'archived': return 'archived'
    default:         return 'unread'
  }
}

/**
 * True if a thread should be visible under the current active-workspace filter.
 *
 * Prefer `taggedOrganizationIds` when present (chat-workspace-tagging): show if any
 * tagged org overlaps the active set. Untagged threads keep the legacy rules below
 * (backward compat until backfill) - same policy as ChatsSidebarPanel.
 *
 * User-level threads (e.g. workspace invite notifications) resolve to the 'personal'
 * sentinel and are always visible.
 */
export function isThreadWorkspaceVisible(
  workspaceId: string,
  activeWorkspaceIds: string[],
  taggedOrganizationIds?: string[] | null,
): boolean {
  if (activeWorkspaceIds.length === 0) return true

  const tags = taggedOrganizationIds ?? []
  if (tags.length > 0) {
    return tags.some((id) => activeWorkspaceIds.includes(id))
  }

  if (workspaceId === 'personal') return true
  return activeWorkspaceIds.includes(workspaceId)
}

/** Convenience wrapper for Thread-shaped rows (Inbox feed / page). */
export function isInboxThreadVisible(
  thread: { workspaceId: string; taggedOrganizationIds?: string[] },
  activeWorkspaceIds: string[],
): boolean {
  return isThreadWorkspaceVisible(
    thread.workspaceId,
    activeWorkspaceIds,
    thread.taggedOrganizationIds,
  )
}

/** Primary workspace for list chrome/label: first tagged org, else workspaceId. */
export function resolveThreadWorkspace(
  thread: { workspaceId: string; taggedOrganizationIds?: string[] },
  workspaces: InboxWorkspace[],
): InboxWorkspace | undefined {
  for (const id of thread.taggedOrganizationIds ?? []) {
    const match = workspaces.find((w) => w.id === id)
    if (match) return match
  }
  return workspaces.find((w) => w.id === thread.workspaceId)
}

/**
 * Resolve a human-readable inbox source label.
 * Soul proposals historically stored provenance as metadata.source (object);
 * inbox must never render that via String(obj) -> "[object Object]".
 */
export function resolveInboxSourceLabel(
  rawSource: unknown,
  kind?: string | Kind,
): string {
  const kindStr = kind != null ? String(kind) : ''

  if (typeof rawSource === 'string') {
    const s = rawSource.trim()
    if (s === '[object Object]') {
      return kindStr === 'soul_proposal' ? 'Soul proposal' : 'System'
    }
    if (!s || s === 'chat_stream_ensure') {
      if (kindStr === 'agent_chat' || kindStr === 'chat') return 'Chat'
      if (kindStr === 'soul_proposal') return 'Soul proposal'
      return ''
    }
    return s
  }

  if (rawSource && typeof rawSource === 'object') {
    if (kindStr === 'soul_proposal') return 'Soul proposal'
    const tool = (rawSource as { tool?: unknown }).tool
    if (typeof tool === 'string' && tool.trim()) {
      if (tool === 'proposeSoulUpdate') return 'Soul proposal'
      return tool
    }
    return kindStr === 'agent_chat' || kindStr === 'chat' ? 'Chat' : 'System'
  }

  if (kindStr === 'soul_proposal') return 'Soul proposal'
  if (kindStr === 'agent_chat' || kindStr === 'chat') return 'Chat'
  return ''
}

/** Human-readable source label (hides internal ensure keys). */
export function formatThreadSource(source: string, kind?: string | Kind): string {
  return resolveInboxSourceLabel(source, kind)
}

function normalizeTaggedOrgIds(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const ids = raw.map((id) => String(id)).filter(Boolean)
  return ids.length > 0 ? ids : undefined
}

/** Map InboxItem -> Thread */
export function mapInboxItemToThread(item: InboxItem): Thread {
  const meta = item.meta ?? {}
  const kind = mapKind(item.type, meta)
  const preview = toInboxPreviewPlain(
    pickInboxPreviewSource({
      summary: meta.summary,
      bodyPreview: meta.body_preview,
      preview: meta.preview,
      snippet: meta.snippet,
      body: item.body,
    }),
    140,
  )
  const taggedFromMeta = normalizeTaggedOrgIds(meta.taggedOrganizationIds)
  const sourceKind =
    meta.threadKind != null ? String(meta.threadKind) : kind

  return {
    id: item.id,
    kind,
    title: toInboxTitlePlain(item.title ?? ''),
    preview,
    body: item.body ?? '',
    source: kind === 'invite'
      ? 'Invite'
      : resolveInboxSourceLabel(item.source ?? meta.source, sourceKind),
    age: formatAge(item.timestamp),
    status: mapStatus(item.status),
    workspaceId: (meta.workspaceId as string) ?? 'personal',
    taggedOrganizationIds: item.taggedOrganizationIds ?? taggedFromMeta,
    extensionId: (meta.extensionId as string) ?? undefined,
    audioDuration: meta.duration != null ? String(meta.duration) : undefined,
    audioFilename: item.audioFilename ?? undefined,
    receivedAt: item.timestamp,
    linkedDocs: (meta.linkedDocs as string[]) ?? undefined,
    linkedConversationId: item.linkedConversationId ?? undefined,
    flowName: (meta.flowName as string) ?? undefined,
    metadata: Object.keys(meta).length > 0 ? meta : undefined,
  }
}

/**
 * Map a /api/threads row directly to Thread (bypassing InboxItem).
 * Use this when you have the raw API response and want to skip the double-map.
 */
export function mapThreadsRowToThread(row: Record<string, unknown>): Thread {
  const meta = (row.metadata ?? {}) as Record<string, unknown>;
  const kind = String(row.kind ?? 'suggestion');
  const isWorkspaceInvite = meta.organization_invite_id != null
  const normalizedKind =
    kind === 'agent_chat' ? 'chat'
      : kind === 'extension_event' ? 'extension'
      : isWorkspaceInvite ? 'invite'
      : kind === 'flow_output' || kind === 'system' ? 'flow'
      : kind === 'soul_proposal' ? 'suggestion'
      : kind
  const validKind: Kind = ['voice','email','agent','suggestion','invite','chat','flow','extension'].includes(normalizedKind)
    ? normalizedKind as Kind
    : 'suggestion'

  const enrichedMeta: Record<string, unknown> = {
    ...meta,
    threadKind: meta.threadKind ?? kind,
    ...(kind === 'soul_proposal' && meta.kind == null ? { kind: 'soul_proposal' } : {}),
  }

  return {
    id: String(row.id),
    kind: validKind,
    title: toInboxTitlePlain(String(row.title ?? '')),
    preview: toInboxPreviewPlain(
      pickInboxPreviewSource({
        summary: meta.summary,
        bodyPreview: meta.body_preview,
        preview: meta.preview,
        snippet: meta.snippet,
        body: meta.body,
      }),
      140,
    ),
    body: String(meta.body_preview ?? meta.body ?? ''),
    source: isWorkspaceInvite
      ? 'Invite'
      : formatThreadSource(String(meta.source ?? ''), kind === 'soul_proposal' ? kind : validKind),
    age: formatAge(String(row.lastMessageAt ?? row.last_message_at ?? row.createdAt ?? '')),
    status: mapThreadStatusDirect(String(row.status ?? 'unread')),
    workspaceId: String(
      row.workspaceId ??
        row.workspace_id ??
        meta.workspaceId ??
        meta.inbox_workspace_org_id ??
        (kind !== 'agent_chat' ? (row.organizationId ?? row.organization_id) : null) ??
        'personal'
    ),
    taggedOrganizationIds: normalizeTaggedOrgIds(
      row.taggedOrganizationIds ?? row.tagged_organization_ids,
    ),
    extensionId: meta.extensionId as string | undefined,
    audioDuration: meta.duration != null ? String(meta.duration) : undefined,
    audioFilename: meta.audio_filename as string | undefined,
    receivedAt: String(
      row.lastMessageAt ?? row.last_message_at ?? row.createdAt ?? row.created_at ?? '',
    ),
    linkedDocs: meta.linkedDocs as string[] | undefined,
    linkedConversationId: (row.linkedConversationId
      ?? row.linked_conversation_id
      ?? meta.linked_conversation_id) as string | undefined,
    flowName: meta.flow_name as string | undefined,
    pinned: Boolean(row.pinned),
    metadata: Object.keys(enrichedMeta).length > 0 ? enrichedMeta : undefined,
  };
}

/** Convert threads.status value to Thread Status enum for UI display. */
function mapThreadStatusDirect(s: string): Status {
  if (s === 'archived') return 'archived';
  if (s === 'read' || s === 'cleared') return 'read';
  return 'unread';
}

// ---------------------------------------------------------------------------
// Workspace mapper - converts app Workspace to InboxWorkspace for the UI
// ---------------------------------------------------------------------------

const WORKSPACE_ICON_MAP: Record<string, React.ElementType> = {
  personal: User,
  work: Building2,
  client: Building2,
  community: Building2,
  other: Building2,
}

export function mapWorkspaceToInbox(
  ws: Workspace,
  activeWorkspaceIds: string[]
): InboxWorkspace {
  const icon = WORKSPACE_ICON_MAP[ws.type] ?? Building2
  return {
    id: ws.id,
    label: ws.name,
    icon,
    active: activeWorkspaceIds.includes(ws.id),
    extensions: [], // Phase 1: no extension shortcuts
    /** Hex from API (`organizations.color`); default matches API seed. */
    color: ws.color ?? '#6366f1',
  }
}
